import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, takeUntil } from "rxjs";
import { ITokens } from "src/app/epicare-public/login/domain/interfaces/login.interface";
import { IOrganizationAccess } from "src/app/shared/interfaces/access.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConnectionEventsService } from "src/app/shared/services/connection-events.service";
import { SessionService } from "src/app/shared/services/session.service";
import { IAcceptInvitation, IInvitationDetails, InvitationAcceptErrorCode } from "../../domain/interfaces/invitation.interface";
import { InvitationService } from "../../domain/services/invitation.service";

enum InvitationStep {
  Loading = "loading",
  Error = "error",

  // Email already belongs to another organization/account.
  KnownEmail = "known-email",

  // Email is new.
  Choice = "choice",
  Login = "login",
  Register = "register",

  Accepting = "accepting",
}

enum InvitationChoice {
  Login = "login",
  Register = "register",
}

@Component({
  selector: "app-invitation-page",
  templateUrl: "./invitation-page.component.html",
  styleUrls: ["./invitation-page.component.scss"],
})
export class InvitationPageComponent implements OnInit, OnDestroy {
  public readonly InvitationStep = InvitationStep;
  public readonly InvitationChoice = InvitationChoice;

  /**
   * Scrolls the section revealed by the chosen option (login or register) into view as soon as
   * it appears, so the user doesn't have to scroll to it manually.
   */
  @ViewChild("activeChoiceSection")
  set activeChoiceSectionSetter(el: ElementRef<HTMLElement> | undefined) {
    if (el) {
      el.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  public step: InvitationStep = InvitationStep.Loading;
  public selectedChoice?: InvitationChoice;

  public token = "";
  public invitation?: IInvitationDetails;

  /**
   * Email to use for this organization when the invited email
   * is already used by the account.
   */
  public emailForOrganization = "";

  /**
   * True when the invitation could not be loaded because it expired (or was revoked),
   * as opposed to a generic/unexpected error. Toggles the error state's message.
   */
  public invitationExpired = false;

  /**
   * True when accepting the invitation failed because the logged-in user is already a
   * member of the invitation's organization. Toggles the error state's message.
   */
  public alreadyMember = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationService: InvitationService,
    private sessionService: SessionService,
    private translateService: TranslateService,
    private apiService: ApiService,
    private connectionEventsService: ConnectionEventsService
  ) {}

  /**
   * Clears any previous session, then switches to the url's language (if any) and loads the
   * invitation matching the url's token each time that token changes, or shows the error state
   * when there is none.
   */
  ngOnInit(): void {
    // Disconnect any user that was connected before handling this invitation - otherwise a
    // stale/expired session could trigger the login dialog while this page is still loading.
    this.sessionService.clear();

    /**
     * Subscribed rather than read once from the snapshot: the token can change
     * (e.g. the user opens a newer invitation link while this page is already open),
     * and the whole invitation must then be reloaded from scratch for the new token.
     */
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy$)).subscribe((queryParams) => {
      this.token = queryParams.get("token") ?? "";
      const lang = queryParams.get("lang");

      // Show the page in the same language as the invitation email. Not persisted: it is sent
      // as the new account's language on registration, and the language returned on acceptance
      // is the one stored for the session.
      if (lang) {
        this.translateService.use(lang);
      }

      if (!this.token) {
        this.step = InvitationStep.Error;
        this.invitationExpired = false;
        this.alreadyMember = false;
        this.scrollToTop();
        return;
      }

      this.loadInvitation();
    });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Resets the page's state and fetches the invitation's details for the current token, moving
   * to the KnownEmail or Choice step depending on whether its email is already in use.
   */
  private loadInvitation(): void {
    this.step = InvitationStep.Loading;
    this.invitation = undefined;
    this.selectedChoice = undefined;
    this.emailForOrganization = "";
    this.invitationExpired = false;
    this.alreadyMember = false;

    /**
     * Token verification must be suspended while this call is in flight: there is no real
     * session yet at this point, and a stale/expired token left over from a previous session
     * must not trigger the refresh flow (and, on refresh failure, the inline login dialog)
     * while this page is still loading the invitation.
     */
    this.apiService.suspendVerifyTokensFlow();

    this.invitationService
      .getInvitationDetails(this.token)
      .pipe(
        takeUntil(this.onDestroy$),
        // Guarantees resumeVerifyTokensFlow() runs even if this subscription is torn down
        // before the response arrives.
        finalize(() => this.apiService.resumeVerifyTokensFlow())
      )
      .subscribe({
        next: (invitation) => {
          this.invitation = invitation;

          if (invitation.emailAlreadyInUse) {
            /**
             * Important:
             *
             * We do NOT try to validate the JWT here.
             * The user must first authenticate through app-login-form.
             *
             * After authentication, we ask for the email that will be
             * associated with this organization.
             */
            this.step = InvitationStep.KnownEmail;
          } else {
            this.step = InvitationStep.Choice;
          }
        },
        error: (err) => {
          this.invitationExpired = err?.status === 403;
          this.step = InvitationStep.Error;
          this.scrollToTop();
        },
      });
  }

  /**
   * Records the user's choice and moves to the matching Login or Register step.
   * @param choice Whether the user wants to log in with an existing account or register a new one
   */
  public selectChoice(choice: InvitationChoice): void {
    this.selectedChoice = choice;

    if (choice === InvitationChoice.Login) {
      this.step = InvitationStep.Login;
    }

    if (choice === InvitationChoice.Register) {
      this.step = InvitationStep.Register;
    }
  }

  /**
   * Called by app-login-form once the user has successfully logged in with an
   * already-existing account.
   *
   * app-login-form is bound with [persistSession]="false" here, so this login does NOT
   * persist its tokens as the real session: navigating away at this point must not leave
   * the user "logged in" without having actually accepted the invitation. The tokens are
   * only used right away to authenticate the acceptInvitation call below - it is that
   * call's response (scoped to the invitation's organization) that gets persisted as the
   * real session, in acceptInvitation()'s success handler.
   * @param tokens Tokens of the existing account, used to authenticate the acceptInvitation call
   */
  public loginTokensReceived(tokens: ITokens): void {
    this.acceptInvitation(
      {
        password: null,
        lang: null,
        email: null,
      },
      tokens.accessToken
    );
  }

  /**
   * Called after the user entered the email to use for this organization.
   * @param email The email entered by the user
   */
  public knownEmailSubmit(email: string): void {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      return;
    }

    this.emailForOrganization = normalizedEmail;

    this.acceptInvitation({
      password: null,
      lang: this.translateService.currentLang,
      email: normalizedEmail,
    });
  }

  /**
   * Called when creating a new account directly through the invitation.
   * @param password The password chosen for the new account
   */
  public registerSubmit(password: string): void {
    this.acceptInvitation({
      password,
      lang: this.translateService.currentLang,
      email: this.invitation?.data.email,
    });
  }

  /**
   * Accepts the invitation, then persists the returned session and navigates to the landing page,
   * or shows the matching error state on failure.
   * @param invitation The supplementary invitation information
   * @param jwt The access token obtained by logging in with an already-existing account
   */
  private acceptInvitation(invitation: IAcceptInvitation, jwt?: string): void {
    this.step = InvitationStep.Accepting;
    this.invitationExpired = false;
    this.alreadyMember = false;

    this.invitationService
      .acceptInvitation(this.token, invitation, jwt)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (value: IOrganizationAccess) => {
          if (!value.tokens.accessToken || !value.tokens.refreshToken) {
            this.step = InvitationStep.Error;
            this.scrollToTop();
            return;
          }

          this.translateService.use(value.language);
          this.connectionEventsService.notifyLogin(value);
          this.router.navigate(["/landing"]);
        },
        error: (err) => {
          this.invitationExpired = err?.status === 403;
          this.alreadyMember = err?.status === 409 && err?.error === InvitationAcceptErrorCode.ALREADY_MEMBER;
          this.step = InvitationStep.Error;
          this.scrollToTop();
        },
      });
  }

  /**
   * Smoothly scrolls the window back to the top of the page.
   */
  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
